import React, { useEffect, useRef, useCallback } from 'react';
import { gsap } from 'gsap';
import { Observer } from 'gsap/Observer';
import { maskLines } from '../../hooks/useMotion.js';
import { reducedMotion } from '../../hooks/useLenis.js';
import './Hero.css';

const AUTOPLAY_MS = 8000;

const slidesData = [
  {
    title: (
      <>
        Welcome to <span className="hero-mark">ASTRID FIRST</span> Wealth Advisory
      </>
    ),
    // The firm name always gets its own line ("Welcome to / ASTRID FIRST / Wealth
    // Advisory"), which free wrapping only produced at desktop widths.
    stacked: true,
    body: 'We build wealth management strategies around your goals, not around a product shelf. Our team is committed to exceptional service and to guiding you towards financial success with precision and care.',
  },
  {
    title: (
      <>
        Transforming <span className="hero-mark">Your Wealth</span>, One Strategy at a Time
      </>
    ),
    body: 'Wealth management solutions shaped to your financial goals and aspirations, with strategies designed to secure what you have built and what comes next.',
  },
  {
    title: (
      <>
        Navigating <span className="hero-mark">Financial Success</span> Together
      </>
    ),
    body: 'Your financial well-being sets our priorities. Explore a comprehensive suite of services and a team whose experience is measured in decades, not quarters.',
  },
  {
    title: (
      <>
        Your Partner in <span className="hero-mark">Strategic Wealth</span> Management
      </>
    ),
    body: 'Expertise meets personal service. Our senior professionals work to optimise your investments and safeguard what you pass on.',
  },
  {
    title: (
      <>
        <span className="hero-mark">Innovative Solutions</span> for Your Financial Success
      </>
    ),
    body: 'Global expertise applied personally. We combine institutional research with independent judgement to make the most of your financial potential.',
  },
];

export const Hero = () => {
  const rootRef = useRef(null);
  const currentIndexRef = useRef(0);
  const timerRef = useRef(undefined);
  const revertingRef = useRef(undefined);

  const goToSlide = useCallback((next, immediate = false) => {
    const root = rootRef.current;
    if (!root) return;

    const slides = Array.from(root.querySelectorAll('[data-slide]'));
    const dots = Array.from(root.querySelectorAll('[data-dot]'));
    if (!slides.length) return;

    const fromIndex = currentIndexRef.current;
    currentIndexRef.current = next;

    const from = slides[fromIndex];
    const to = slides[next];

    dots.forEach((dot, i) =>
      dot.setAttribute('aria-selected', String(i === next))
    );
    slides.forEach((slide, i) => {
      slide.setAttribute('data-active', String(i === next));
      slide.setAttribute('aria-hidden', String(i !== next));
    });

    if (immediate || reducedMotion()) {
      gsap.set(slides, { autoAlpha: 0 });
      gsap.set(to, { autoAlpha: 1 });
      return;
    }

    revertingRef.current?.();
    revertingRef.current = undefined;

    gsap.to(from, { autoAlpha: 0, duration: 0.45, ease: 'power2.inOut' });
    gsap.set(to, { autoAlpha: 1 });

    const heading = to.querySelector('h1');
    const body = to.querySelector('p');

    if (heading) {
      const { revert } = maskLines(heading, { duration: 0.9, stagger: 0.08 });
      revertingRef.current = revert;
    }
    if (body) {
      gsap.fromTo(
        body,
        { autoAlpha: 0, y: 14 },
        { autoAlpha: 1, y: 0, duration: 0.7, delay: 0.15, ease: 'power2.out' }
      );
    }
  }, []);

  const restartTimer = useCallback(() => {
    window.clearInterval(timerRef.current);
    if (reducedMotion()) return;
    timerRef.current = window.setInterval(() => {
      const next = (currentIndexRef.current + 1) % slidesData.length;
      goToSlide(next);
    }, AUTOPLAY_MS);
  }, [goToSlide]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const slides = Array.from(root.querySelectorAll('[data-slide]'));
    if (!slides.length) return;

    gsap.set(slides.slice(1), { autoAlpha: 0 });
    gsap.set(slides[0], { autoAlpha: 1 });

    if (!reducedMotion()) {
      const heading = slides[0].querySelector('h1');
      if (heading) {
        const { revert } = maskLines(heading, { duration: 0.85, delay: 0.15 });
        revertingRef.current = revert;
      }
    }

    const observer = Observer.create({
      target: root,
      type: 'touch,pointer',
      dragMinimum: 40,
      tolerance: 40,
      onLeft: () => {
        const next = (currentIndexRef.current + 1) % slidesData.length;
        goToSlide(next);
        restartTimer();
      },
      onRight: () => {
        const next = (currentIndexRef.current - 1 + slidesData.length) % slidesData.length;
        goToSlide(next);
        restartTimer();
      },
      preventDefault: false,
    });

    // Autoplay pauses only while someone is reading or using the controls. It used
    // to pause on hover over the whole hero, which fills the screen, so on desktop
    // the pointer was nearly always inside it and the slides never advanced. The
    // zones are the text elements themselves: .hero-deck spans the full width too.
    const pauseZones = Array.from(
      root.querySelectorAll('[data-slide] h1, [data-slide] p, [data-slide] .btn, .hero-dots')
    );
    const pausedBy = new Set();
    let inView = true;
    const update = () => {
      if (pausedBy.size || !inView || document.hidden) window.clearInterval(timerRef.current);
      else restartTimer();
    };
    const pause = (reason) => () => { pausedBy.add(reason); update(); };
    const resume = (reason) => () => { pausedBy.delete(reason); update(); };
    const listeners = pauseZones.flatMap((zone) => [
      [zone, 'pointerenter', pause(zone)],
      [zone, 'pointerleave', resume(zone)],
    ]);
    listeners.push(
      [root, 'focusin', pause('focus')],
      [root, 'focusout', (e) => { if (!root.contains(e.relatedTarget)) resume('focus')(); }],
      [document, 'visibilitychange', update]
    );
    listeners.forEach(([el, type, fn]) => el.addEventListener(type, fn));

    const viewObserver = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      update();
    });
    viewObserver.observe(root);

    restartTimer();

    return () => {
      window.clearInterval(timerRef.current);
      revertingRef.current?.();
      observer.kill();
      viewObserver.disconnect();
      listeners.forEach(([el, type, fn]) => el.removeEventListener(type, fn));
    };
  }, [goToSlide, restartTimer]);

  return (
    <section className="hero" data-hero ref={rootRef}>
      <div
        className="hero-media"
        data-slot="Hero video — 1920×1080 loop, or a still image"
      >
        <video
          data-autoplay
          autoPlay
          muted
          loop
          playsInline
          src="/media/hero-background.mp4"
          poster="/media/singapore-cityscape-at-night-2026-09-15-20-13-22-utc.webp"
        />
      </div>

      <div className="hero-inner">
        <div className="hero-deck">
          {slidesData.map((slide, i) => (
            <article
              key={i}
              className="hero-slide"
              data-slide
              data-active={i === 0 ? 'true' : 'false'}
              aria-hidden={i !== 0}
            >
              <h1 className={slide.stacked ? 'hero-title--stacked' : undefined}>{slide.title}</h1>
              <p>{slide.body}</p>
              <a className="btn" href="/services">
                Explore our services
              </a>
            </article>
          ))}
        </div>

        <div className="hero-foot">
          <div className="hero-dots" role="tablist" aria-label="Headlines">
            {slidesData.map((_, i) => (
              <button
                key={i}
                type="button"
                data-dot
                aria-selected={i === 0}
                aria-label={`Headline ${i + 1}`}
                onClick={() => {
                  if (currentIndexRef.current === i) return;
                  goToSlide(i);
                  restartTimer();
                }}
              />
            ))}
          </div>
          <span className="hero-hint">
            Licensed by the Monetary Authority of Singapore
          </span>
        </div>
      </div>
    </section>
  );
};